import { FC, useState, useEffect } from "react";
import {
    BaseGrid,
    Box,
    Container,
    GridItem,
    Text,
} from "@smile2impress/components/relaunch";
import { useRouter } from "next/router";
import {
    BlocksRenderer,
    type BlocksContent,
} from "@strapi/blocks-react-renderer";
import { useCampaignContext } from "context/campaignContext";

import { Logo } from "components/Logo";
import { UrlsConfig } from "configs/urlsConfig";

import { FooterColumn } from "./components/FooterColumn";
import { FooterSocial } from "./components/FooterSocial";
import { FooterCopyright } from "./components/FooterCopyright";
import { FooterProps } from "./Footer.types";
import { FooterLink } from "./components/FooterLink";

export const Footer: FC<FooterProps> = ({
    title,
    company,
    product,
    support,
    disclaimerContent,
    copyright,
    socialNetworks,
    legal,
    workingHours,
    footnote1,
    footnote2,
    containerProps,
    hideFooterMenu = false,
}) => {
    const { pathname } = useRouter();
    const [campaignDisclaimerContent, setCampaignDisclaimerContent] = useState<
        BlocksContent | undefined
    >(undefined);
    const { campaign: campaignFromContext } = useCampaignContext();
    const isSmileVisionOfferPage = pathname.startsWith(
        UrlsConfig.SMILE_VISION_OFFER
    );

    const smileVisionDisclaimerContent = isSmileVisionOfferPage ? (
        <>
            {(footnote1 || footnote2) && (
                <Text
                    as="p"
                    textStyle="captionSmall"
                    opacity="0.5"
                    dangerouslySetInnerHTML={{
                        __html: [footnote1, footnote2]
                            .filter((str?: string) => str?.length)
                            .join("<br/>"),
                    }}
                    sx={{
                        _dark: {
                            a: {
                                color: "var(--chakra-colors-text-base);",
                            },
                        },
                    }}
                />
            )}
        </>
    ) : null;

    useEffect(() => {
        if (campaignFromContext) {
            const { disclaimer } = campaignFromContext;
            if (disclaimer) {
                setCampaignDisclaimerContent(disclaimer);
            }
        }
    }, [campaignFromContext]);

    const iterableColumns = [company, product, legal]
        .filter((col) => col.links.length > 0)
        .map((col, index) => <FooterColumn {...col} key={`fc-${index}`} />);

    const renderColumn = (index: number) => {
        return [
            ...iterableColumns,
            <>
                <Box>
                    <Text textStyle="title3" mb={2}>
                        {support.title}
                    </Text>
                    {support.links.map((props, index) => (
                        <FooterLink {...props} key={index} />
                    ))}
                </Box>
                <FooterSocial socialNetworks={socialNetworks} />
                <Text mt={[2]} textStyle="bodySmall" color="text.base">
                    {workingHours.weekdays}
                </Text>
                <Text mt={[2]} textStyle="bodySmall" color="text.base">
                    {workingHours.weekends}
                </Text>
            </>,
        ][index];
    };

    return (
        <Container
            variant="base"
            bgColor="base.grey"
            _dark={{
                bgColor: "base.greyDark",
            }}
            px={[2, 2, 4]}
            pt={[3, 3, 4]}
            pb={[5, 5, 4]}
            {...containerProps}
        >
            <BaseGrid>
                <GridItem colSpan={13} mb={[3, 3, 4]}>
                    <Logo isRelaunch />
                </GridItem>
                {title && (
                    <GridItem rowStart={2} colSpan={13} mb={3}>
                        <Text
                            textStyle="title2"
                            fontSize={["26px", "26px", 36]}
                        >
                            {title}
                        </Text>
                    </GridItem>
                )}
                {!isSmileVisionOfferPage && !hideFooterMenu && (
                    <>
                        <GridItem rowStart={3} colStart={1} colSpan={4}>
                            {renderColumn(0)}
                        </GridItem>

                        <GridItem
                            rowStart={[4, 3]}
                            colStart={[1, 5, 4]}
                            colSpan={4}
                            mt={[2, 0]}
                        >
                            {renderColumn(1)}
                        </GridItem>

                        <GridItem
                            rowStart={[6, 3]}
                            colStart={[1, 9, 7]}
                            colSpan={4}
                            mt={[2, 0]}
                        >
                            {renderColumn(2)}
                        </GridItem>

                        <GridItem
                            rowStart={[7, 4, 3]}
                            colStart={[1, 1, 10]}
                            colSpan={4}
                            mt={[2, 0]}
                        >
                            {renderColumn(3)}
                        </GridItem>
                    </>
                )}
                <GridItem
                    rowStart={[10, 5]}
                    colStart={1}
                    colSpan={13}
                    textAlign={"left"}
                    mt={!isSmileVisionOfferPage ? 3 : 0}
                >
                    {isSmileVisionOfferPage
                        ? smileVisionDisclaimerContent
                        : disclaimerContent && (
                              <Text
                                  textStyle="captionSmall"
                                  opacity="0.5"
                                  dangerouslySetInnerHTML={{
                                      __html: disclaimerContent,
                                  }}
                                  sx={{
                                      _dark: {
                                          a: {
                                              // Call variable directly because standard "text.base" doesn't work here
                                              color: "var(--chakra-colors-text-base);",
                                          },
                                      },
                                  }}
                              />
                          )}
                    {campaignDisclaimerContent && (
                        <Text
                            textStyle="captionSmall"
                            opacity="0.5"
                            // Use <div> to avoid validateDOMNesting error: <p> cannot appear as a descendant of <p>.
                            as="div"
                            sx={{
                                _dark: {
                                    a: {
                                        // Call variable directly because standard "text.base" doesn't work here
                                        color: "var(--chakra-colors-text-base);",
                                    },
                                },
                            }}
                        >
                            <BlocksRenderer
                                content={
                                    campaignDisclaimerContent as BlocksContent
                                }
                            />
                        </Text>
                    )}
                </GridItem>
                <GridItem
                    rowStart={[11, 6]}
                    colStart={1}
                    colSpan={13}
                    textAlign={"left"}
                    mt={3}
                >
                    <FooterCopyright {...copyright} />
                </GridItem>
            </BaseGrid>
        </Container>
    );
};
